
import { PropType } from 'vue'
import { serialise } from 'kitsu-core'
import DialogTemplate from '@/_components/dialog/template/template.vue'
import Log from '@/views/Log/models/log'
import AlertLogs from '@/views/Project/ch/components/workAlert/log/logItems'
import LogMixin from '@/_mixins/log'
import { getURL } from '@/utils/axios'
import { postRequest, patchRequest } from '@/plugins/api-axios'
import moment from 'moment'
import { cloneDeep } from 'lodash'

const LogNumber = '32'

export default {
    name: 'AlertLogDialog',
    components: { AlertLogs, DialogTemplate },
    mixins: [LogMixin],
    props: {
        value: {
            type: Boolean,
        },
        props: {
            type: Object as PropType<{
                entityId: string
                hasUnread: boolean
                companyName: string
            }>,
            required: true,
        },
    },
    data() {
        return {
            logs: [],
            logReadState: {},
            timeStamp: new Date().toISOString() as string | null,
            tabIndex: this.props.hasUnread ? 0 : 1,
            readEventIdentifier: Date.now(),
            unreadEventIdentifier: Date.now(),
            isMounted: false,
            hasAlertMarkedAsRead: false,
            markAllAsReadDialog: false,
            selectedLogs: {
                type: '',
                items: [],
            },
            loadingMarkedAllAsRead: false,
        }
    },
    methods: {
        handleTabChanged(tabIndex: number) {
            this.logs = []
            this.timeStamp = new Date().toISOString()
            if (tabIndex === 0) this.unreadEventIdentifier = Date.now()
            else this.readEventIdentifier = Date.now()
        },
        toggleMarkAllAsReadDialog(visible: boolean, logData?: any) {
            this.markAllAsReadDialog = visible
            if (!visible) {
                this.selectedLogs.type = ''
                this.selectedLogs.items = []
            } else {
                this.selectedLogs.type = logData.type
                this.selectedLogs.items = logData.items
            }
        },
        async markAllAsRead(logData: any) {
            this.loadingMarkedAllAsRead = true
            const type = logData && logData.type ? logData.type : ''
            let params = {
                'filter[activityLogEntityId]': this.currentProjectId,
                'filter[activityLogNumber]': [LogNumber],
                'filter[entityId]': this.props.entityId,
            }
            const yesterday = moment().subtract(1, 'days')
            const today = moment()
            const tomorrow = moment().add(1, 'days')

            switch (type) {
                case 'today':
                    params['filter[time][gt]'] = yesterday.toISOString()
                    params['filter[time][lt]'] = tomorrow.toISOString()
                    break
                case 'yesterday':
                    params['filter[time][gt]'] = yesterday.toISOString()
                    break
                case 'thisWeek':
                    const startOfWeek = today.clone().startOf('week')
                    const endOfWeek = today.clone().endOf('week')
                    params['filter[time][ge]'] = startOfWeek.toISOString()
                    params['filter[time][le]'] = endOfWeek.toISOString()
                    break
                case 'lastWeek':
                    const dateLastWeek = today.clone().subtract(1, 'weeks')
                    const startOfLastWeek = dateLastWeek.clone().startOf('week')
                    const endOfLastWeek = dateLastWeek.clone().endOf('week')
                    params['filter[time][ge]'] = startOfLastWeek.toISOString()
                    params['filter[time][le]'] = endOfLastWeek.toISOString()
                    break
                case 'thisMonth':
                    const startOfMonth = today.clone().startOf('month')
                    const endOfMonth = today.clone().endOf('month')
                    params['filter[time][ge]'] = startOfMonth.toISOString()
                    params['filter[time][le]'] = endOfMonth.toISOString()
                    break
                case 'lastMonth':
                    const dateLastMonth = today.clone().startOf('month').subtract(1, 'months')
                    const startOfLastMonth = dateLastMonth.clone().startOf('month')
                    const endOfLastMonth = dateLastMonth.clone().endOf('month')
                    params['filter[time][ge]'] = startOfLastMonth.toISOString()
                    params['filter[time][le]'] = endOfLastMonth.toISOString()
                    break
                case 'older':
                    const dateStartOfLastMonth = today
                        .clone()
                        .startOf('month')
                        .subtract(1, 'months')
                    const dateOlderThanLastMonth = dateStartOfLastMonth.subtract(1, 'days')
                    params['filter[time][le]'] = dateOlderThanLastMonth.toISOString()
                    break
                default:
                    break
            }

            await postRequest(
                getURL({
                    path: '/markAllAsRead',
                    base: this.logURL,
                    params,
                }),
                {}
            )
            this.hasAlertMarkedAsRead = true

            this.loadingMarkedAllAsRead = false
            this.toggleMarkAllAsReadDialog(false)

            if (type) {
                logData.items.forEach((id: string) => {
                    const itemIndex = this.logs.findIndex((log) => log._id === id)
                    let temp = this.logReadState
                    temp[id] = true
                    this.logReadState = cloneDeep(temp)
                    if (this.tabIndex === 1) this.logs.splice(itemIndex, 1)
                })
            }

            this.logs = []
            if (this.tabIndex === 0) {
                this.unreadEventIdentifier = Date.now()
            }
            if (this.tabIndex === 1) {
                this.readEventIdentifier = Date.now()
            }
            this.$emit('doAction', {
                hasAlertMarkedAsRead: this.hasAlertMarkedAsRead,
            })
        },
        logRead(logId: string, index: number) {
            this.hasAlertMarkedAsRead = true
            this.logReadState[logId] = true
            if (this.tabIndex === 0) this.logs.splice(index, 1)
            this.$emit('doAction', {
                hasAlertMarkedAsRead: true,
            })
        },
        logUnRead(logId: string, index: number) {
            this.hasAlertMarkedAsRead = true
            this.logReadState[logId] = false
            if (this.tabIndex === 1) this.logs.splice(index, 1)
            this.$emit('doAction', {
                hasAlertMarkedAsRead: true,
            })
        },
        closeDialog(goToManage = false) {
            this.$emit('close', {
                hasAlertMarkedAsRead: this.hasAlertMarkedAsRead,
                goToManage,
            })
        },
        async getLogs($state: any) {
            const params = {
                'filter[activityLogEntityId]': this.currentProjectId,
                'filter[activityLogNumber]': [LogNumber],
                'filter[entityId]': this.props.entityId,
                ...(this.tabIndex === 0 && { 'filter[isRead]': false }),
                ...(this.tabIndex === 1 && { 'filter[isRead]': true }),
                ...(this.timeStamp && { 'filter[time][lt]': this.timeStamp }),
            }

            let [logResponse, logReadStateResponse] = await Promise.all([
                Log.getLog({ ...params }).catch(() => {
                    $state.complete()
                }),
                // fetch read state
                this.tabIndex === 1
                    ? Log.getLog({
                          ...params,
                          'fields[event]': 'isRead',
                      })
                    : Promise.resolve([]),
            ])

            this.logs.push(...logResponse)
            logReadStateResponse.forEach((log) => {
                this.$set(this.logReadState, log._id, log.isRead)
            })

            if (logResponse.length === Log.limit) {
                let lastElement = logResponse?.pop()
                this.timeStamp = lastElement ? lastElement?.['time'] : null
                $state.loaded()
            } else {
                this.timeStamp = null
                if (this.logs.length > 0) {
                    $state.loaded()
                }
                $state.complete()
            }
        },
    },
    mounted() {
        this.isMounted = true
    },
    destroyed() {
        this.isMounted = false
        this.tabIndex = 0
    },
}
